import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled || attempts >= 8) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 1500);
      } catch (err) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="py-32 bg-white min-h-[60vh]" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {status === "checking" && (
          <>
            <div className="overline">One moment&hellip;</div>
            <h1 className="font-serif text-5xl text-navy mt-3">Confirming your payment</h1>
            <p className="mt-5 text-charcoal/70">Please don't close this window.</p>
            <div className="mt-10 inline-block w-10 h-10 border-2 border-navy/20 border-t-gold rounded-full animate-spin" />
          </>
        )}
        {status === "paid" && (
          <>
            <div className="overline">Confirmed</div>
            <h1 className="font-serif text-5xl text-navy mt-3" data-testid="payment-success-title">Thank you.</h1>
            <p className="mt-5 text-charcoal/85 leading-relaxed">
              {tx?.type === "donation"
                ? "Your gift to the 1123 Foundation has been received. A receipt is on its way to your inbox."
                : "Your order is confirmed. A confirmation email has been sent and a team member will reach out with shipping details shortly."}
            </p>
            <div className="ornament-rule mt-12 max-w-xs mx-auto"><span>&#10086;</span></div>
            <div className="mt-10 flex gap-4 justify-center">
              <Link to="/" className="bg-navy text-ivory px-7 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal">Return Home</Link>
              <Link to="/bookstore" className="border border-navy/30 text-navy px-7 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-navy hover:text-ivory transition-colors">Continue Reading</Link>
            </div>
          </>
        )}
        {(status === "timeout" || status === "expired" || status === "error") && (
          <>
            <div className="overline">Pending</div>
            <h1 className="font-serif text-5xl text-navy mt-3">We're still finalizing.</h1>
            <p className="mt-5 text-charcoal/75">If you completed checkout, your confirmation will arrive by email. If there was an issue, you can try again.</p>
            <Link to="/" className="inline-block mt-10 bg-navy text-ivory px-7 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal">Return Home</Link>
          </>
        )}
      </div>
    </div>
  );
}
